import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Info, Sparkles } from "lucide-react";
import useSEO from "@/lib/useSEO";
import api from "@/lib/api";
import {
  TIERS,
  MOVE_PRICING,
  ADDONS,
  PRICING_DISCLAIMER,
} from "@/data/content";
import { usePublicSettings } from "@/contexts/SettingsContext";

export default function Pricing() {
  useSEO({ slug: "pricing", title: "Cleaning Service Pricing in Sacramento | P & A Cleaning" });

  const settings = usePublicSettings();
  const [addons, setAddons] = useState(ADDONS);
  const [moveItems, setMoveItems] = useState(MOVE_PRICING);
  const [pricingDisclaimer, setPricingDisclaimer] = useState("");

  useEffect(() => {
    api.get("/public/addons").then((r) => {
      const list = (r.data || [])
        .filter((a) => a.active !== false)
        .map((a) => ({ label: a.name, price: a.price_range }));
      if (list.length > 0) setAddons(list);
    }).catch(() => {});
    api.get("/public/pricing").then((r) => {
      const moves = (r.data || [])
        .filter((p) => p.category === "move")
        .map((p) => ({ label: p.title, price: p.price_range }));
      if (moves.length > 0) setMoveItems(moves);
      const d = (r.data || []).find((p) => p.disclaimer);
      if (d?.disclaimer) setPricingDisclaimer(d.disclaimer);
    }).catch(() => {});
  }, []);

  // Website Settings (Admin) win; pricing-row disclaimer is a secondary source;
  // static constant is the final fallback.
  const disclaimer =
    settings.pricing_disclaimer || pricingDisclaimer || PRICING_DISCLAIMER;

  return (
    <div data-testid="page-pricing">
      {/* HERO */}
      <section className="bg-gradient-to-b from-[#F8FAFC] to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-[#1E3A8A] shadow-soft">
            <Sparkles className="w-3.5 h-3.5 text-[#34D399]" /> Ballpark estimates
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-[#0F172A] tracking-tight">
            Transparent pricing,{" "}
            <span className="gradient-text">no surprises</span>
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-lg text-[#475569]">
            Prices are quoted per job, not hourly. Use the ranges below to
            ballpark — final pricing is confirmed after a quick review.
          </p>
        </div>
      </section>

      {/* TIERS */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {TIERS.map((t) => (
              <div
                key={t.name}
                data-testid={`pricing-tier-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`relative rounded-3xl p-8 border transition-all flex flex-col ${
                  t.popular
                    ? "bg-[#1E3A8A] text-white border-[#1E3A8A] shadow-card -translate-y-1"
                    : "bg-white text-[#0F172A] border-slate-100 shadow-soft hover:shadow-card hover:-translate-y-1"
                }`}
              >
                {t.popular && (
                  <span className="absolute -top-3 left-8 px-3 py-1 rounded-full text-[10px] font-semibold tracking-wider uppercase bg-[#34D399] text-[#0F172A]">
                    Most Popular
                  </span>
                )}
                <span
                  className={`inline-flex w-fit items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase ${
                    t.popular ? "bg-white/15 text-white" : "bg-[#A78BFA]/10 text-[#7C3AED]"
                  }`}
                >
                  {t.badge}
                </span>
                <h2 className={`mt-4 font-display font-semibold text-2xl ${t.popular ? "text-white" : "text-[#0F172A]"}`}>
                  {t.name}
                </h2>
                <p className={`mt-2 text-sm ${t.popular ? "text-white/80" : "text-[#475569]"}`}>
                  {t.tagline}
                </p>
                <ul className={`mt-6 space-y-3 text-sm ${t.popular ? "text-white/90" : "text-[#0F172A]"}`}>
                  {t.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${t.popular ? "text-[#34D399]" : "text-[#34D399]"}`} />
                      {b}
                    </li>
                  ))}
                </ul>
                <p className={`mt-6 text-xs ${t.popular ? "text-white/70" : "text-[#475569]"}`}>
                  {t.note}
                </p>
                <Link
                  to="/request-estimate"
                  data-testid={`pricing-tier-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}-cta`}
                  className={`mt-7 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold transition-colors ${
                    t.popular
                      ? "bg-white text-[#1E3A8A] hover:bg-slate-100"
                      : "bg-[#1E3A8A] text-white hover:bg-[#1E40AF]"
                  }`}
                >
                  {t.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MOVE PRICING */}
      <section className="py-12 lg:py-20 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-soft" data-testid="pricing-move-table">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#1E3A8A]">
              Move-In / Move-Out / Property Cleaning
            </p>
            <h2 className="mt-2 text-2xl font-display font-semibold text-[#0F172A]">
              Typical ranges by home size
            </h2>
            <ul className="mt-6 divide-y divide-slate-100">
              {moveItems.map((row) => (
                <li key={row.label} className="flex items-center justify-between py-3.5 gap-4">
                  <span className="text-sm text-[#0F172A] font-medium">{row.label}</span>
                  <span className="text-sm font-semibold text-[#1E3A8A] text-right">{row.price}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ADDONS */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-8">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#7C3AED]">
              Add-ons
            </p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0F172A]">
              Customize your clean
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pricing-addons-grid">
            {addons.map((row) => (
              <div
                key={row.label}
                data-testid={`pricing-addon-${row.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="bg-[#F8FAFC] border border-slate-100 rounded-2xl p-5 flex items-start justify-between gap-3"
              >
                <span className="text-sm font-medium text-[#0F172A] leading-snug">
                  {row.label}
                </span>
                <span className="text-sm font-semibold text-[#1E3A8A] text-right whitespace-nowrap-fix">
                  {row.price}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            data-testid="pricing-disclaimer"
            className="bg-[#1E3A8A]/5 border border-[#1E3A8A]/10 rounded-3xl p-7 lg:p-9 flex items-start gap-4"
          >
            <Info className="w-6 h-6 text-[#1E3A8A] flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-display font-semibold text-[#0F172A]">
                A quick note about pricing
              </h3>
              <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                {disclaimer}
              </p>
              <Link
                to="/request-estimate"
                data-testid="pricing-disclaimer-cta"
                className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors"
              >
                Request an Estimate <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
